import { z } from 'zod'
import { JobCostingRollupFiltersSchema } from './jobCostingDtos'

/**
 * =============================================================================
 * Technician Performance DTOs
 * =============================================================================
 *
 * DTOs for the technician performance table on the GP/Man-Day dashboard.
 * Provides per-technician metrics including job counts, financial attribution,
 * and time breakdown from timesheets.
 *
 * References:
 * - PRD REQ-F5-001..028
 * - TRD §7.2.2
 */

/**
 * Request schema for technician performance endpoint.
 * Uses the same filters as the rollup endpoint for consistency.
 */
export const JobCostingTechnicianPerformanceRequestSchema = z.object({
  filter: JobCostingRollupFiltersSchema,
})
export type JobCostingTechnicianPerformanceRequest = z.infer<typeof JobCostingTechnicianPerformanceRequestSchema>

/**
 * Individual technician performance row schema.
 *
 * Calculation notes (TRD §7.3):
 * - capacityMinutes = sum of all capacity segment durations for this technician
 * - manDays = capacityMinutes / 480 (8-hour workday)
 * - gpPerManDayUsc = grossProfitUsc / manDays (null when manDays = 0)
 * - avgSalesUsc = totalSalesUsc / jobsCount
 * - Time excludes LUNCH activity type
 *
 * Attribution rules (PRD REQ-F5-011/012):
 * - Multi-technician jobs: full financial attribution to each tech
 * - Time metrics: only that technician's actual timesheet hours
 * - Capacity: real availability from CapacityCalculationService
 */
export const TechnicianPerformanceRowSchema = z.object({
  // Technician identity
  userGuid: z.string().uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),

  // Job count
  jobsCount: z.number().int().nonnegative(),

  // Financial metrics (all in USC - US Cents)
  totalSalesUsc: z.number().int().nonnegative(),
  avgSalesUsc: z.number().int().nonnegative(),
  grossProfitUsc: z.number().int(), // Can be negative (losses)

  // Labor and efficiency metrics
  laborCostsUsc: z.number().int().nonnegative(),
  capacityMinutes: z.number().int().nonnegative(), // Real capacity from CapacityCalculationService
  manDays: z.number().nonnegative(), // Derived: capacityMinutes / 480 (8-hour day)
  gpPerManDayUsc: z.number().int().nullable(), // null when manDays = 0

  // Time breakdown from timesheets (in minutes)
  totalMinutes: z.number().int().nonnegative(), // ON-SITE + DRIVE-TIME + IDLE
  onSiteMinutes: z.number().int().nonnegative(), // ON-SITE activity
  driveMinutes: z.number().int().nonnegative(), // DRIVE-TIME activity
  idleMinutes: z.number().int().nonnegative(), // IDLE activity
})
export type TechnicianPerformanceRow = z.infer<typeof TechnicianPerformanceRowSchema>

/**
 * Response schema for technician performance endpoint.
 * Returns array of technician rows; empty array when no data for period.
 */
export const TechnicianPerformanceResponseSchema = z.array(TechnicianPerformanceRowSchema)
export type TechnicianPerformanceResponse = z.infer<typeof TechnicianPerformanceResponseSchema>
